import { Middleware, ResponseContext } from '@euler/lib/request';
import { makeDebug } from '@euler/utils';

const debug = makeDebug('auth:token-refresh:middleware', __DEV__);

type RefreshResult = {
  token: string;
  refreshToken: string;
  refreshTokenExpiresIn: number;
};

type RefreshAttemptResult =
  | { status: 'succeeded'; token: string }
  | {
      status:
        | 'missing-refresh-token'
        | 'expired-refresh-token'
        | 'refresh-token-rejected'
        | 'refresh-failed';
    };

/**
 * Tells a refresh token the server has definitively disowned — revoked,
 * rotated away, or speaking for an account that no longer exists — from a
 * refresh that merely failed to complete.
 *
 * The verdict is the error CODE, not the HTTP status: the server answers
 * `invalid_refresh_token` with whatever status its error table happens to map
 * the code to, so keying off 401 alone would read a dead session as a
 * retryable hiccup and leave the app 401ing forever.
 */
function isRefreshTokenRejection(e: unknown): boolean {
  const { code, status } = (e ?? {}) as { code?: string; status?: number };
  return code === 'invalid_refresh_token' || status === 401 || status === 403;
}

export class TokenRefreshMiddleware implements Middleware {
  private refreshPromise: Promise<RefreshAttemptResult> | null = null;

  constructor(
    private readonly getRefreshToken: () => Promise<string | null>,
    private readonly performRefresh: (rt: string) => Promise<RefreshResult>,
    private readonly onRefreshResult: (result: RefreshResult) => Promise<void>,
    private readonly getRefreshTokenExpiresAt?: () => Promise<number | null>,
    private readonly getAccessToken?: () => Promise<string | null>,
  ) {}

  async post(context: ResponseContext): Promise<void> {
    if (context.response.status !== 401) return;
    const headers = context.init.headers as Record<string, string> | undefined;
    if (!headers?.['Authorization']) return;
    context.authRefreshStatus = 'not-attempted';
    const startedAt = Date.now();

    debug(
      '401 received for %s, attempting reactive token refresh',
      context.url,
    );
    const result = await this.refreshOnce();
    context.authRefreshStatus = result.status;
    if (result.status !== 'succeeded') {
      const latestToken = await this.getAccessToken?.();
      if (
        result.status === 'refresh-token-rejected' &&
        latestToken &&
        headers.Authorization !== `Bearer ${latestToken}`
      ) {
        debug(
          'refresh token was rotated elsewhere, retrying with latest token after %dms',
          Date.now() - startedAt,
        );
        context.authRefreshStatus = 'succeeded';
        context.init.headers = {
          ...headers,
          Authorization: `Bearer ${latestToken}`,
        };
        context.response = await fetch(context.url, context.init);
        debug(
          'latest-token retry completed with status %d for %s',
          context.response.status,
          context.url,
        );
        return;
      }
      debug(
        'reactive refresh failed (%s) after %dms, request will remain unauthorized',
        result.status,
        Date.now() - startedAt,
      );
      return;
    }

    debug(
      'reactive refresh succeeded after %dms, retrying request',
      Date.now() - startedAt,
    );
    context.init.headers = {
      ...headers,
      Authorization: `Bearer ${result.token}`,
    };
    context.response = await fetch(context.url, context.init);
    debug(
      'reactive retry completed with status %d for %s',
      context.response.status,
      context.url,
    );
  }

  private async refreshOnce(): Promise<RefreshAttemptResult> {
    if (this.refreshPromise) {
      debug('refresh already in flight, deduplicating');
      // eslint-disable-next-line @typescript-eslint/return-await
      return this.refreshPromise;
    }
    this.refreshPromise = this.doRefresh().finally(() => {
      this.refreshPromise = null;
    });
    // eslint-disable-next-line @typescript-eslint/return-await
    return this.refreshPromise;
  }

  private async doRefresh(): Promise<RefreshAttemptResult> {
    const refreshToken = await this.getRefreshToken();
    if (!refreshToken) {
      debug('no refresh token available');
      return { status: 'missing-refresh-token' };
    }
    const refreshTokenExpiresAt = await this.getRefreshTokenExpiresAt?.();
    debug(
      'refresh token loaded (expiresAt: %s)',
      refreshTokenExpiresAt
        ? new Date(refreshTokenExpiresAt).toLocaleString()
        : 'unknown',
    );
    if (refreshTokenExpiresAt != null && refreshTokenExpiresAt <= Date.now()) {
      debug(
        'refresh token has expired (expiresAt: %s)',
        new Date(refreshTokenExpiresAt).toLocaleString(),
      );
      return { status: 'expired-refresh-token' };
    }
    try {
      const startedAt = Date.now();
      debug('calling refresh endpoint');
      const result = await this.performRefresh(refreshToken);
      await this.onRefreshResult(result);
      debug(
        'token rotated successfully in %dms (next refresh expiresIn: %ds)',
        Date.now() - startedAt,
        result.refreshTokenExpiresIn,
      );
      return { status: 'succeeded', token: result.token };
    } catch (e) {
      debug('token rotation failed: %o', e);
      return {
        status: isRefreshTokenRejection(e)
          ? 'refresh-token-rejected'
          : 'refresh-failed',
      };
    }
  }
}
